import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { _isNotBlank } from '@/littledash';
import { DateUtils } from '@/utils/Date.utils';
import FormField from '@/components/UI/FormFields/Latest/FormField';
import { TimepointInput } from '@/components/UI/Timepoint/TimepointInput';

interface TimepointInputProps {
  name: string;
  label: string;
  hint?: string;
  allow_zero?: boolean;
  allow_negative?: boolean;
}

const Timepoint: FC<TimepointInputProps> = ({ name, label, hint, allow_zero, allow_negative }) => {
  const { control, setError, clearErrors } = useFormContext();
  return (
    <FormField label={label} name={name} hint={hint}>
      <Controller
        control={control}
        name={name}
        defaultValue=""
        render={({ field: { value, onChange, onBlur }, fieldState: { invalid } }) => (
          <TimepointInput
            value={value}
            invalid={invalid}
            onChange={(change) => {
              onChange(change.value);
              if (change.parseError) {
                setError(name, { type: 'manual', message: 'Invalid timepoint format' });
              } else if (_isNotBlank(change.value)) {
                const validationType = DateUtils.validateDurationRange(change.value, { allow_zero, allow_negative });
                if (validationType.valid) {
                  clearErrors(name);
                } else {
                  switch (validationType.reason) {
                    case 'error_format':
                      setError(name, { type: 'manual', message: 'Invalid timepoint format' });
                      break;
                    case 'error_range_max':
                      setError(name, { type: 'manual', message: 'Timepoint exceeds maximum allowed value' });
                      break;
                    case 'error_range_min':
                      setError(name, { type: 'manual', message: 'Timepoint is below minimum allowed value' });
                      break;
                    default:
                      setError(name, { type: 'manual', message: `Timepoint invalid [${validationType.reason}]` });
                      break;
                  }
                }
              } else {
                clearErrors(name);
              }
            }}
            onBlur={onBlur}
          />
        )}
      />
    </FormField>
  );
};

export default Timepoint;
